import { Group, HoverCard, Stack, Text } from '@mantine/core';
import { IconWifi, IconWifiOff } from '@tabler/icons-react';

import { statusMap } from '@/store/selectors/transmissionReportSelectors';
import formatDate from '@/utils/formatDate';

import styles from './DeviceStatus.module.scss';

type ConnectivityStatus = 'Connected' | 'Disconnected' | 'Inductive';

interface DeviceLike {
  id: string;
  type?: string;
  status?: string | number;
  modelName?: string;
  serialNumber?: string;
  vendor?: string;
  connectivityStatus?: ConnectivityStatus | null;
  lastTransmissionAt?: string | null;
  nextTransmissionAt?: string | null;
}

interface DeviceStatusProps {
  devices?: DeviceLike[];
}

const INACTIVE_STATUS = new Set(['inactive', 'removed', 'entered in error', '0', '4']);

const isActiveDevice = (device: DeviceLike) => {
  const raw = String(device.status ?? '').trim().toLowerCase();
  const mapped = statusMap[Number(device.status)]?.toLowerCase();
  const label = mapped || raw;
  return !INACTIVE_STATUS.has(raw) && !INACTIVE_STATUS.has(label);
};

const isLive = (status?: ConnectivityStatus | null) =>
  status === 'Connected' || status === 'Inductive';

const DeviceStatus = ({ devices }: DeviceStatusProps) => {
  const active = (Array.isArray(devices) ? devices : []).filter(isActiveDevice).slice(0, 2);
  if (!active.length) return null;

  return (
    <Group gap={6} wrap="nowrap">
      {active.map((device) => {
        const status = device.connectivityStatus ?? null;
        const live = isLive(status);
        return (
          <HoverCard key={device.id} width={250} shadow="md" position="bottom-start" withArrow openDelay={80}>
            <HoverCard.Target>
              <div
                className={styles.chip}
                data-connected={live ? 'true' : status === 'Disconnected' ? 'false' : 'unknown'}>
                {live ? <IconWifi size={14} stroke={2} /> : <IconWifiOff size={14} stroke={2} />}
                <span className={styles.type}>{device.type ?? 'Device'}</span>
              </div>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Stack gap={6}>
                <Group justify="space-between" gap={8} wrap="nowrap">
                  <Text fw={700} fz="sm" c="plum.9">
                    {device.type ?? 'Device'}
                  </Text>
                  {status && (
                    <Text fz="xs" fw={700} c={live ? 'teal.7' : 'red.6'}>
                      {status}
                    </Text>
                  )}
                </Group>
                {device.modelName && (
                  <Text fz="xs" c="plum.7">
                    {device.modelName}
                  </Text>
                )}
                {device.serialNumber && (
                  <Text fz="xs" c="plum.6">
                    Serial {device.serialNumber}
                  </Text>
                )}
                <Group justify="space-between" gap={8} wrap="nowrap">
                  <Text fz="11px" fw={600} c="plum.6">
                    Last transmission
                  </Text>
                  <Text fz="xs" fw={600} c="plum.8">
                    {device.lastTransmissionAt ? formatDate(device.lastTransmissionAt) : '—'}
                  </Text>
                </Group>
                <Group justify="space-between" gap={8} wrap="nowrap">
                  <Text fz="11px" fw={600} c="plum.6">
                    Next transmission
                  </Text>
                  <Text fz="xs" fw={600} c="plum.8">
                    {device.nextTransmissionAt ? formatDate(device.nextTransmissionAt) : '—'}
                  </Text>
                </Group>
              </Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        );
      })}
    </Group>
  );
};

export default DeviceStatus;
